import * as React from "react";

import { cn } from "@/lib/utils";

function TableContainer({
  className,
  children,
  scrollRef,
  footer,
  ...props
}: React.ComponentProps<"div"> & {
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  footer?: React.ReactNode;
}) {
  return (
    <div
      data-slot="table-container"
      className={cn(
        "flex h-full flex-col overflow-hidden rounded-[12px] border-neutral-200",
        className,
      )}
      {...props}
    >
      {/* Scrollable table area */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <div
          ref={scrollRef}
          className="scrollbar-thin h-full w-full overflow-x-auto overflow-y-auto"
          // style={{ scrollbarGutter: "stable" }}
        >
          <div className="min-w-full">{children}</div>
        </div>
      </div>

      {/* Fixed footer */}
      {footer && (
        <div className="flex-shrink-0 border-t border-neutral-200">
          {footer}
        </div>
      )}
    </div>
  );
}

function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <table
      data-slot="table"
      className={cn(
        "w-full table-fixed caption-bottom border-collapse text-sm",
        className,
      )}
      {...props}
    />
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("border-b border-neutral-200", className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody data-slot="table-body" className={cn("", className)} {...props} />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "bg-muted/50 border-t border-neutral-200 font-medium [&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "data-[state=selected]:bg-muted border-b border-neutral-200 transition-colors",
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "text-foreground h-10 border-r border-neutral-200 px-2 text-left align-middle font-medium whitespace-nowrap last:border-r-0 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "h-[37px] border-r border-neutral-200 p-2 align-middle last:border-r-0 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  );
}

export {
  Table,
  TableContainer,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
